import { useQuery } from '@tanstack/react-query';

import { api } from '@shared/api/client';
import { key, STALE_TIME } from '@shared/api/query-keys';

import type { CaseStateRow } from './types';

/**
 * The **only** wire source for the case states, read by both surfaces — which is
 * why it lives here rather than on either of them. The queue reads it to know
 * whether a row's token is one the platform currently configures; the detail
 * reads it to fill its state control, in the configured order.
 *
 * The vocabulary is a `support__configurations` value and this route exists
 * precisely so no surface has to know its members: «a surface that hard-codes
 * these four is a defect». Every row still carries its own token, so a value
 * outside the set renders neutral with its literal label rather than crashing or
 * being dropped.
 *
 * Cached for the vocabulary window — it changes about once a quarter, and a
 * configuration write elsewhere in the console invalidates this exact tuple.
 */
export function caseStatesKey() {
  return key('support', 'caseStates');
}

export function useCaseStates() {
  return useQuery({
    queryKey: caseStatesKey(),
    queryFn: () => api.get<CaseStateRow[]>('support', '/caseStates'),
    staleTime: STALE_TIME.vocabulary,
    // Neither surface falls over without it: the queue degrades every badge to
    // its literal token, and the detail says the control is unavailable rather
    // than offering a list it cannot vouch for.
    throwOnError: false,
  });
}

/**
 * The caller's own permissions **on `backend-support`**.
 *
 * It gates exactly one control: the decision's block-owner rider, which the
 * owning service refuses to an `operator` with `403
 * SUPPORT_BLOCK_REQUIRES_ADMIN`. Rendering a checkbox the server will refuse
 * teaches an operator that the console lies to them, so the box is drawn from
 * the permission set — and the refusal still ships, because **hiding is UX and
 * never security**.
 *
 * It gates a control and nothing else: not a data read, not an authorization
 * outcome, and never a computed answer about what the caller may do.
 */
export function supportPermissionsKey() {
  return key('support', 'permissions');
}

interface PermissionsPayload {
  role: { name: string; permissions?: string[] };
}

export function useSupportPermissions() {
  return useQuery({
    queryKey: supportPermissionsKey(),
    queryFn: () => api.get<PermissionsPayload>('support', '/me/permissions'),
    staleTime: STALE_TIME.vocabulary,
    // A failed permission read hides the rider rather than taking the page
    // down: the decision itself does not depend on it.
    throwOnError: false,
  });
}
